<template>
  <div class="autocomplete-wrapper" :class="{'autocomplete-wrapper-error': error}" >
    <div class="input-container" :class="{ 'focused': isFocused }">
      <VCustomInput
          v-model="searchText"
          @focus="isFocused = true"
          @blur="handleBlur"
          @click="openSelect"
          @keydown.arrow-down="highlightNext"
          @keydown.arrow-up="highlightPrev"
          @keydown.enter="selectHighlighted"
          @input="onInputChange"
          :placeholder="placeholder"
          class="autocomplete-input"
          :disabled="disabled"
          no-clear
      />
      <button v-if="searchText && !disabled" @click="clearInput" class="clear-btn" :class="{'v-input__icon_disabled': disabled}">
        <img src="@/assets/images/icons/x.svg" alt="close" />
      </button>
    </div>
    <ul v-if="showSuggestions && filteredItems.length > 0" class="suggestions-list">
      <li
          v-for="(item, index) in filteredItems"
          :key="index"
          @mousedown="selectItem(item)"
          :class="{ 'highlighted': index === highlightedIndex }"
          class="suggestion-item"
      >
        {{ itemText ? item[itemText] : item }}
      </li>
    </ul>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';

const props = defineProps({
  items: {
    type: Array,
    required: true
  },
  itemText: {
    type: String,
    default: ''
  },
  itemValue: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: 'Type to search...'
  },
  modelValue: {
    type: [String, Number, Object],
    default: null
  },
  disabled: {
    type: Boolean,
    default: false
  },
  error: {
    type: Boolean,
  }
});

const emit = defineEmits(['update:modelValue', 'input']);

const searchText = ref('');
const isFocused = ref(false);
const highlightedIndex = ref(-1);
const showSuggestions = ref(false);

const filteredItems = computed(() => {
  if (!searchText.value) return props.items;
  return props.items.filter(item => {
    const text = props.itemText ? item[props.itemText] : item;
    return text.toLowerCase().includes(searchText.value.toLowerCase());
  });
});

const openSelect = () => {
  if(props.disabled) return
  showSuggestions.value = !showSuggestions.value;
}

watch(() => props.modelValue, (newVal) => {
  if (newVal) {
    const item = props.items.find(i =>
        props.itemValue ? i[props.itemValue] === newVal : i === newVal
    );
    if (item) {
      searchText.value = props.itemText ? item[props.itemText] : item;
    }
  } else {
    searchText.value = '';
  }
}, { immediate: true });

function onInputChange() {
  showSuggestions.value = true;
  highlightedIndex.value = -1;
  emit('input', searchText.value);
}

function selectItem(item) {
  const value = props.itemValue ? item[props.itemValue] : item;
  searchText.value = props.itemText ? item[props.itemText] : item;
  emit('update:modelValue', value);
  showSuggestions.value = false;
}

function clearInput() {
  searchText.value = '';
  emit('update:modelValue', null);
  showSuggestions.value = false;
}

function handleBlur() {
  setTimeout(() => {
    isFocused.value = false;
    showSuggestions.value = false;
  }, 200);
}

function highlightNext() {
  if (highlightedIndex.value < filteredItems.value.length - 1) {
    highlightedIndex.value++;
  }
}

function highlightPrev() {
  if (highlightedIndex.value > 0) {
    highlightedIndex.value--;
  }
}

function selectHighlighted() {
  if (highlightedIndex.value >= 0 && filteredItems.value[highlightedIndex.value]) {
    selectItem(filteredItems.value[highlightedIndex.value]);
  }
}
</script>

<style lang="scss" scoped>
.autocomplete-wrapper {
  position: relative;
  border-radius: 8px;
  width: 100%;
  font-family: 'Arial', sans-serif;
  background-color: $light;
}

.autocomplete-wrapper-error {
  border: solid 1px red;
}

.input-container {
  position: relative;
  transition: border-color 0.3s;
}

.autocomplete-input {
  width: 100%;
  border: none;
  outline: none;
  font-size: 16px;
  height: 48px;
  padding-right: 25px;


    :deep(.v-input__input){
      overflow-x: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }

}

.clear-btn {
  position: absolute;
  cursor: pointer;
  right: 4px;
  top: 55%;
  transform: translateY(-50%);
  background: none;
  border: none;
  font-size: 18px;
  color: #999;
  padding: 0 5px;

  &:hover {
    color: #333;
  }
}

.suggestions-list {
  position: absolute;
  width: 100%;
  max-height: 200px;
  overflow-y: auto;
  margin: 4px 0 0;
  padding: 8px;
  list-style: none;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  scrollbar-width: thin;
}

.suggestion-item {
  height: 40px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;
  padding: 12px 8px 4px;
  font-size: 14px;
  overflow-x: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  &:hover, &.highlighted {
    background-color: $light-gray-1;
    color: $dark;
  }
}
</style>